import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount, listGLAccounts, listBankFeedConnections, createBankFeedConnection, deleteBankFeedConnection, syncBankFeed } from '../api';
import { Sync as RefreshCw, Link as LinkIcon, LinkOff as Unlink } from '@mui/icons-material';
import { useConfirm } from './ConfirmProvider';

function BankAccountSettings({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [feedConnections, setFeedConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const emptyForm = {
    account_name: '', bank_name: '', account_number: '', branch_code: '',
    swift_code: '', currency: 'BWP', gl_account: '',
    is_default: false, is_active: true, opening_balance: '0.00', opening_balance_date: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [accts, gl, feeds] = await Promise.all([
        listBankAccounts(authAxios),
        listGLAccounts(authAxios),
        listBankFeedConnections(authAxios).catch(() => []),
      ]);
      setAccounts(Array.isArray(accts) ? accts : []);
      setGLAccounts(Array.isArray(gl) ? gl.filter(a => a.category === 'ASSET') : []);
      setFeedConnections(Array.isArray(feeds) ? feeds : []);
    } catch {
      showFormMessage('error', 'Failed to load bank accounts.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // opening_balance_date is nullable on the backend (DateField(null=True)),
      // but DRF rejects an empty string for a DateField — only `null` or a
      // real date. Leaving the field blank previously sent `''` and 400'd
      // unconditionally, even though the date is optional.
      const payload = {
        ...formData,
        gl_account: formData.gl_account || null,
        opening_balance_date: formData.opening_balance_date || null,
      };
      if (editingAccount) {
        await updateBankAccount(authAxios, editingAccount.id, payload);
        showFormMessage('success', 'Bank account updated.');
      } else {
        await createBankAccount(authAxios, payload);
        showFormMessage('success', 'Bank account created.');
      }
      setShowForm(false);
      setEditingAccount(null);
      setFormData(emptyForm);
      fetchData();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save bank account.');
    }
  };

  const handleEdit = (acct) => {
    setEditingAccount(acct);
    setFormData({
      account_name: acct.account_name || '',
      bank_name: acct.bank_name || '',
      account_number: acct.account_number || '',
      branch_code: acct.branch_code || '',
      swift_code: acct.swift_code || '',
      currency: acct.currency || 'BWP',
      gl_account: acct.gl_account || '',
      is_default: acct.is_default || false,
      is_active: acct.is_active !== false,
      opening_balance: acct.opening_balance || '0.00',
      opening_balance_date: acct.opening_balance_date || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete Bank Account', message: 'Delete this bank account?', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteBankAccount(authAxios, id);
      showFormMessage('success', 'Bank account deleted.');
      fetchData();
    } catch {
      showFormMessage('error', 'Failed to delete bank account.');
    }
  };

  const handleConnectFeed = async (accountId) => {
    if (!(await confirm({ title: 'Connect Bank Feed', message: 'Connect a mock bank feed to this account for testing?', confirmText: 'Connect' }))) return;
    try {
      await createBankFeedConnection(authAxios, {
        bank_account: accountId,
        provider: 'MOCK',
        provider_item_id: 'mock_item_' + accountId,
        provider_access_token: 'mock_token',
        status: 'ACTIVE'
      });
      showFormMessage('success', 'Bank feed connected successfully.');
      fetchData();
    } catch {
      showFormMessage('error', 'Failed to connect bank feed.');
    }
  };

  const handleDisconnectFeed = async (feedId) => {
    if (!(await confirm({ title: 'Disconnect Bank Feed', message: 'Are you sure you want to disconnect this feed?', confirmText: 'Disconnect', variant: 'danger' }))) return;
    try {
      await deleteBankFeedConnection(authAxios, feedId);
      showFormMessage('success', 'Bank feed disconnected.');
      fetchData();
    } catch {
      showFormMessage('error', 'Failed to disconnect bank feed.');
    }
  };

  const handleSyncFeed = async (feedId) => {
    setIsSyncing(prev => ({ ...prev, [feedId]: true }));
    try {
      const res = await syncBankFeed(authAxios, feedId);
      showFormMessage('success', res.message || 'Bank feed synced successfully.');
    } catch {
      showFormMessage('error', 'Failed to sync bank feed. Check connection or try again.');
    } finally {
      setIsSyncing(prev => ({ ...prev, [feedId]: false }));
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Bank Accounts</h2>
        {canManage && (
          <button
            onClick={() => { setEditingAccount(null); setFormData(emptyForm); setShowForm(true); }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
          >+ Add Bank Account</button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white border rounded shadow-lg p-4 mb-6">
          <h3 className="font-semibold mb-3">{editingAccount ? 'Edit' : 'New'} Bank Account</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account Name *</label>
              <input name="account_name" value={formData.account_name} onChange={handleChange} required
                className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. Primary Cheque" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name *</label>
              <input name="bank_name" value={formData.bank_name} onChange={handleChange} required
                className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. First National Bank" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account Number *</label>
              <input name="account_number" value={formData.account_number} onChange={handleChange} required
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Branch Code</label>
              <input name="branch_code" value={formData.branch_code} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SWIFT Code</label>
              <input name="swift_code" value={formData.swift_code} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
              <input name="currency" value={formData.currency} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GL Account</label>
              <select name="gl_account" value={formData.gl_account} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm">
                <option value="">-- Select GL Account --</option>
                {glAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.code} – {a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Opening Balance</label>
              <input name="opening_balance" type="number" step="0.01" value={formData.opening_balance} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Opening Balance Date</label>
              <input name="opening_balance_date" type="date" value={formData.opening_balance_date} onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div className="flex items-center gap-4 pt-5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_default" checked={formData.is_default} onChange={handleChange} />
                Default Account
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                Active
              </label>
            </div>
            <div className="md:col-span-2 flex gap-2 mt-2">
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
                {editingAccount ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingAccount(null); }}
                className="bg-gray-200 px-4 py-2 rounded text-sm hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Accounts List */}
      {isLoading ? (
        <p className="text-gray-500 text-sm">Loading...</p>
      ) : accounts.length === 0 ? (
        <p className="text-gray-400 text-sm italic">No bank accounts configured yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Account Name</th>
                <th className="px-4 py-2 text-left">Bank</th>
                <th className="px-4 py-2 text-left">Account #</th>
                <th className="px-4 py-2 text-left">GL Account</th>
                <th className="px-4 py-2 text-center">Default</th>
                <th className="px-4 py-2 text-center">Active</th>
                <th className="px-4 py-2 text-center">Bank Feed</th>
                {canManage && <th className="px-4 py-2 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {accounts.map(acct => {
                const feed = feedConnections.find(f => f.bank_account === acct.id);
                return (
                  <tr key={acct.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{acct.account_name}</td>
                    <td className="px-4 py-2">{acct.bank_name}</td>
                    <td className="px-4 py-2">{acct.account_number}</td>
                    <td className="px-4 py-2">{acct.gl_account_code ? `${acct.gl_account_code} – ${acct.gl_account_name}` : '—'}</td>
                    <td className="px-4 py-2 text-center">{acct.is_default ? '✓' : ''}</td>
                    <td className="px-4 py-2 text-center">{acct.is_active ? '✓' : '✗'}</td>
                    <td className="px-4 py-2 text-center inline-flex items-center justify-center gap-2">
                      {feed ? (
                        <>
                          <span className="text-green-600 font-semibold text-xs py-1 px-2 bg-green-100 rounded">Linked</span>
                          <button onClick={() => handleSyncFeed(feed.id)} disabled={isSyncing[feed.id]}
                            className={`text-gray-600 hover:text-blue-600 ${isSyncing[feed.id] ? 'animate-spin' : ''}`}
                            title="Sync Feed">
                            <RefreshCw sx={{ fontSize: 16 }} />
                          </button>
                          {canManage && (
                            <button onClick={() => handleDisconnectFeed(feed.id)} className="text-red-500 hover:text-red-700" title="Disconnect feed">
                              <Unlink sx={{ fontSize: 16 }} />
                            </button>
                          )}
                        </>
                      ) : (
                        canManage && (
                          <button onClick={() => handleConnectFeed(acct.id)} className="text-gray-500 hover:text-blue-600 text-xs flex items-center gap-1">
                            <LinkIcon sx={{ fontSize: 14 }} /> Connect
                          </button>
                        )
                      )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => handleEdit(acct)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                        <button onClick={() => handleDelete(acct.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BankAccountSettings;
